import { useEffect } from 'react';
import { PageShell } from '@/components/layout/PageShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { NeonButton } from '@/components/ui/NeonButton';
import { Icon } from '@/components/ui/Icon';
import { useCheckout } from '@/modules/billing/useCheckout';
import { usePlan } from '@/modules/billing/usePlan';
import { hasStripe } from '@/config/env';
import { capture } from '@/modules/heat/signals';
import { cn } from '@/lib/cn';

interface TierProps {
  name: string;
  price: string;
  bullets: string[];
  cta: string;
  current?: boolean;
  highlighted?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

const Tier = ({ name, price, bullets, cta, current, highlighted, disabled, onClick }: TierProps) => (
  <div
    className={cn(
      'flex flex-col gap-5 border p-6 md:p-8',
      highlighted ? 'border-pink/40 bg-pink/[0.04] shadow-glow-soft' : 'border-white/[0.10] bg-charcoal/40',
    )}
  >
    <div>
      <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
        {name}
      </p>
      <p className="mt-3 font-display text-4xl font-black uppercase tracking-display text-text-primary">
        {price}
      </p>
    </div>
    <ul className="space-y-2 text-sm text-text-secondary">
      {bullets.map((b) => (
        <li key={b} className="flex items-start gap-2">
          <Icon name="check" size={14} className="mt-1 shrink-0 text-pink" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
    <NeonButton
      type="button"
      size="md"
      variant={highlighted ? 'primary' : 'outline'}
      disabled={disabled || current}
      onClick={onClick}
      className="mt-auto"
      trailing={current ? undefined : <Icon name="arrow_forward" size={14} />}
    >
      {current ? 'Current plan' : cta}
    </NeonButton>
  </div>
);

export const PlansPage = () => {
  const { startCheckout, busy } = useCheckout();
  const { plan } = usePlan();

  useEffect(() => {
    capture('surface_enter', 'plans', undefined, { event: 'plans_view' });
  }, []);

  return (
    <PageShell
      rail={false}
      mobileTopBar={
        <>
          {/* Identity is the WorldSwitcher in PageShell's bar — one lockup.
              This spacer keeps the page label on the right edge. */}
          <span aria-hidden />
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Plans
          </span>
        </>
      }
    >
      <section className="border-b border-white/[0.06] px-6 py-10 md:px-12 md:py-14">
        <SectionMarker className="mb-3">Plans · pick your room</SectionMarker>
        <h1
          className="font-display font-black uppercase tracking-display"
          style={{ fontSize: 'clamp(36px, 5vw, 72px)', lineHeight: 0.9, letterSpacing: '-0.04em' }}
        >
          Premium for the regulars<span className="text-pink">.</span>
          <br />
          <em className="font-light italic text-text-secondary">VIP for the hosts</em>
          <span className="text-pink">.</span>
        </h1>
      </section>

      <div className="mx-auto grid max-w-6xl gap-4 px-6 py-10 md:grid-cols-3 md:px-12 md:py-14">
        <Tier
          name="Free"
          price="A$0"
          bullets={[
            'Feed · grid · map',
            'Direct messaging',
            'Basic Hottie nudges',
            'Block + report',
          ]}
          cta="Start free"
          current={plan === 'free'}
          disabled
        />
        <Tier
          name="Premium"
          price="A$24/mo"
          bullets={[
            'Travel mode — anywhere',
            'Advanced filters · last active',
            'Incognito-lite',
            'Profile view history',
            'Better Hottie tools',
            'Ad-free',
          ]}
          cta="Go premium"
          highlighted
          current={plan === 'premium'}
          disabled={!hasStripe() || busy}
          onClick={() => startCheckout('premium')}
        />
        <Tier
          name="VIP"
          price="A$49/mo"
          bullets={[
            'Everything in Premium',
            'Full stealth mode',
            'Priority placement windows',
            'Expanded travel + creator perks',
            'Premium verification badge',
            'Priority support',
          ]}
          cta="Become VIP"
          current={plan === 'vip'}
          disabled={!hasStripe() || busy}
          onClick={() => startCheckout('vip')}
        />
      </div>

      {!hasStripe() && (
        <p className="mx-auto max-w-3xl px-6 pb-10 text-center text-xs text-text-secondary">
          Billing is not yet configured in this build. Set Stripe envs in
          <code className="mx-1 rounded bg-charcoal/60 px-1.5 py-0.5 font-mono text-[11px] text-text-primary">.env.local</code>
          (see <code className="font-mono text-[11px]">docs/RUNBOOK.md</code>) to enable checkout.
        </p>
      )}
    </PageShell>
  );
};
